/**
 * FilterPanel — Figma's "Filter creators" panel. Everything in the popover
 * is a draft: field edits only touch local state, and the black "Show N
 * creators" button is the one thing that commits it (calling `onChange`)
 * and closes the panel. Re-opening always starts a fresh draft from the
 * last applied `value` — an edit that was never applied is simply gone.
 * Clear all resets the draft the same way a field edit does; it still
 * takes Show N creators to actually apply the cleared state (no frame in
 * Figma says otherwise, and it keeps every field working the same way).
 *
 * "Reached on" stays a date range — Figma's version is a channel filter
 * (Any/Email/Instagram/Not yet), but the BE only has `reached_from`/
 * `reached_to` today (channel is a BE follow-up). "Address on file" is
 * dropped entirely: the BE has no `has_contact` filter param yet, so
 * there's nothing to wire it to (also a BE follow-up).
 */
import { useState } from 'react';
import { useOutreachFilterPreviewCount, type OutreachFilterPreviewParams } from '@discovr/features';
import { OUTREACH_STATES, type OutreachInboxTab, type OutreachState } from '@discovr/types';
import { STOPPED_STAGE_VALUE } from './stageParam';
import { inboxTabStage } from './inboxTabs';
import { SlidersHorizontal, CaretDown } from '../../../design-system/icons';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';

export interface OutreachFilterState {
  reachedFrom?: string;
  reachedTo?: string;
  stage?: string;
  intent?: string;
  lockedRate?: boolean;
}

const STAGE_LABELS: Record<OutreachState, string> = {
  sourced: 'Not contacted',
  contacted: 'Contacted',
  replied: 'Replied',
  rate_provided: 'Rate agreed',
  rejected: 'Rejected',
  not_interested: 'Not interested',
  ghosted: 'Ghosted',
  bounced: 'Bounced',
  unsubscribed: 'Unsubscribed',
  removed: 'Removed',
};

const STAGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  ...OUTREACH_STATES.map((state) => ({ value: state, label: STAGE_LABELS[state] })),
  // Every stopped state including removed: wider than the Stopped tab, which leaves removed to Removed.
  { value: STOPPED_STAGE_VALUE, label: 'Any stopped stage' },
];

const INTENT_OPTIONS = [
  { value: 'quoted_rates', label: 'Quoted rates' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'interested_no_rates', label: 'Interested, no rates' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'question', label: 'Question' },
];

export function activeCount(v: OutreachFilterState): number {
  return [v.reachedFrom || v.reachedTo ? 1 : 0, v.stage ? 1 : 0, v.intent ? 1 : 0, v.lockedRate ? 1 : 0].reduce(
    (a, b) => a + b,
    0,
  );
}

const FIELD_SELECT =
  'h-9 w-full appearance-none rounded-lg border border-border bg-card px-2.5 pr-8 text-sm text-foreground';

function FieldSelect({
  id,
  label,
  value,
  onChange,
  placeholder,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <div className="min-w-0 flex-1">
      <label className="block text-[10px] font-medium tracking-wide text-muted-foreground uppercase" htmlFor={id}>
        {label}
      </label>
      <div className="relative mt-1">
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={FIELD_SELECT}>
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <CaretDown size={14} className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-muted-foreground" />
      </div>
    </div>
  );
}

export interface FilterPanelProps {
  value: OutreachFilterState;
  onChange: (next: OutreachFilterState) => void;
  campaignId: string;
  tab: OutreachFilterPreviewParams['tab'];
  inboxTab: OutreachInboxTab;
  query: string;
}

export function FilterPanel({ value, onChange, campaignId, tab, inboxTab, query }: FilterPanelProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const count = activeCount(value);

  // Fresh draft every time the panel opens — an edit that was never applied
  // (Show N creators never clicked) is discarded, not carried over. Set in
  // the open handler itself, not an effect: this is a response to the user
  // opening the panel, not a reaction to `value`/`open` changing on their own.
  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(value);
    setOpen(next);
  };

  const draftStage = inboxTabStage(inboxTab, draft.stage);
  // A range typed by keyboard can still be reversed; ISO dates compare as strings.
  const reachedReversed = !!draft.reachedFrom && !!draft.reachedTo && draft.reachedTo < draft.reachedFrom;
  const previewParams: OutreachFilterPreviewParams = {
    tab,
    q: query || undefined,
    reached_from: draft.reachedFrom,
    reached_to: draft.reachedTo,
    stage: draftStage ?? undefined,
    intent: draft.intent,
    locked_rate: draft.lockedRate,
  };
  // Same narrowing as the rows query, so the count never disagrees with the list it applies.
  const preview = useOutreachFilterPreviewCount(campaignId, previewParams, open && draftStage !== null && !reachedReversed);
  const total = draftStage === null ? 0 : preview.data?.total;
  const applyLabel = total == null ? 'Show creators' : `Show ${total} ${total === 1 ? 'creator' : 'creators'}`;

  const apply = () => {
    if (reachedReversed) return;
    onChange(draft);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-[13px] font-medium transition-colors',
            count > 0
              ? 'border-foreground bg-foreground text-background'
              : 'border-border bg-card text-foreground hover:bg-muted',
          )}
        >
          <SlidersHorizontal size={20} /> Filters{count > 0 ? ` ${count}` : ''}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-4 p-4" align="end">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Filter creators</span>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={() => setDraft({})}
          >
            Clear all
          </button>
        </div>

        <div>
          <span className="block text-[10px] font-medium tracking-wide text-muted-foreground uppercase">Reached on</span>
          <div className="mt-1 flex items-center gap-2">
            <input
              id="filter-reached-from"
              aria-label="Reached from"
              type="date"
              max={draft.reachedTo}
              value={draft.reachedFrom ?? ''}
              onChange={(e) => setDraft({ ...draft, reachedFrom: e.target.value || undefined })}
              className="h-9 w-full rounded-lg border border-border bg-card px-2 text-sm"
            />
            <span className="text-sm text-muted-foreground">to</span>
            <input
              aria-label="Reached to"
              aria-invalid={reachedReversed || undefined}
              aria-describedby={reachedReversed ? 'filter-reached-error' : undefined}
              type="date"
              min={draft.reachedFrom}
              value={draft.reachedTo ?? ''}
              onChange={(e) => setDraft({ ...draft, reachedTo: e.target.value || undefined })}
              className="h-9 w-full rounded-lg border border-border bg-card px-2 text-sm"
            />
          </div>
          {reachedReversed && (
            <p id="filter-reached-error" role="alert" className="mt-1 text-xs text-destructive">
              The end date is before the start date.
            </p>
          )}
        </div>

        <FieldSelect
          id="filter-stage"
          label="Stage"
          value={draft.stage ?? ''}
          onChange={(v) => setDraft({ ...draft, stage: v || undefined })}
          placeholder="Any stage"
          options={STAGE_OPTIONS}
        />

        <FieldSelect
          id="filter-intent"
          label="Intent"
          value={draft.intent ?? ''}
          onChange={(v) => setDraft({ ...draft, intent: v || undefined })}
          placeholder="Any"
          options={INTENT_OPTIONS}
        />

        <label htmlFor="filter-locked-rate" className="flex items-center gap-2 text-sm text-foreground">
          <input
            id="filter-locked-rate"
            type="checkbox"
            checked={draft.lockedRate ?? false}
            onChange={(e) => setDraft({ ...draft, lockedRate: e.target.checked || undefined })}
          />
          Has a locked rate
        </label>

        <button
          type="button"
          onClick={apply}
          disabled={reachedReversed}
          className="flex h-11 w-full items-center justify-center rounded-full bg-foreground text-sm font-medium text-background disabled:cursor-not-allowed disabled:opacity-50"
        >
          {applyLabel}
        </button>
      </PopoverContent>
    </Popover>
  );
}
